import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Check, RefreshCw, Wallet, History, ArrowRight, Star, Zap, Crown } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { loadRazorpayScript, formatMoney } from '@/lib/razorpay';

const PACKAGE_ICONS = {
  starter: Star,
  pro: Zap,
  premium: Crown,
};

export default function CreditsPage() {
  const { user, credits, refreshCredits, setCreditsLocal } = useAuth();
  const [currency, setCurrency] = useState(null); // auto-detected
  const [packages, setPackages] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [activePkg, setActivePkg] = useState(null);
  const [customCredits, setCustomCredits] = useState(10);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Initialize currency to INR by default. INR orders enable UPI / GPay /
  // NetBanking / wallets in Razorpay Checkout. Users can toggle to USD
  // explicitly if they want to pay with an international card.
  useEffect(() => {
    if (!currency) {
      setCurrency('INR');
    }
  }, [currency]);

  const loadPackages = async (cur) => {
    setLoading(true);
    try {
      const [{ data: pkgData }, { data: cfg }] = await Promise.all([
        api.get(`/credits/packages?currency=${cur}`),
        api.get('/credits/config'),
      ]);
      setPackages(pkgData.packages || []);
      setConfig(cfg);
    } catch (e) {
      toast.error('Could not load credit packages');
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get('/credits/history');
      setHistory(data || []);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (currency) loadPackages(currency);
  }, [currency]);

  useEffect(() => {
    loadHistory();
    refreshCredits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const customAmountDisplay = useMemo(() => {
    if (!customCredits || customCredits < 1) return 0;
    const rate = currency === 'INR' ? 80 : 1;
    return customCredits * rate;
  }, [customCredits, currency]);

  const startCheckout = async ({ packageId, customAmount }) => {
    if (!config?.enabled) {
      toast.error('Payments are not configured. Please contact support.');
      return;
    }
    setPurchasing(true);
    setActivePkg(packageId || 'custom');

    try {
      const ok = await loadRazorpayScript();
      if (!ok || !window.Razorpay) {
        toast.error('Could not load Razorpay. Please check your connection.');
        return;
      }

      const body = packageId
        ? { package_id: packageId, currency }
        : { custom_credits: customAmount, currency };

      const { data: order } = await api.post('/credits/order', body);

      const options = {
        key: order.key_id,
        amount: order.amount_minor,
        currency: order.currency,
        name: order.name || 'Radiance Hub',
        description: order.description || `${order.credits_to_grant} credits`,
        image: 'https://radiancehub.org/icon-192.png',
        order_id: order.order_id,
        prefill: order.prefill,
        theme: { color: '#C2787C' },
        // For INR orders, surface UPI prominently (GPay / PhonePe / Paytm
        // appear under the UPI block automatically). For USD orders Razorpay
        // shows international card form by default.
        ...(order.currency === 'INR'
          ? {
              config: {
                display: {
                  blocks: {
                    upi_block: {
                      name: 'Pay using UPI',
                      instruments: [{ method: 'upi' }],
                    },
                    other_block: {
                      name: 'Other payment methods',
                      instruments: [
                        { method: 'card' },
                        { method: 'netbanking' },
                        { method: 'wallet' },
                      ],
                    },
                  },
                  sequence: ['block.upi_block', 'block.other_block'],
                  preferences: { show_default_blocks: false },
                },
              },
            }
          : {}),
        handler: async (response) => {
          try {
            const { data: verified } = await api.post('/credits/verify', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setCreditsLocal(verified.credits);
            toast.success(`Added ${verified.credits_added} credits! Your new balance is ${verified.credits}.`);
            loadHistory();
          } catch (e) {
            const msg = e?.response?.data?.detail || 'Payment verification failed';
            toast.error(typeof msg === 'string' ? msg : 'Payment verification failed');
          } finally {
            setPurchasing(false);
            setActivePkg(null);
          }
        },
        modal: {
          ondismiss: () => {
            setPurchasing(false);
            setActivePkg(null);
            toast('Payment cancelled', { description: 'No charge was made.' });
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => {
        const msg = resp?.error?.description || 'Payment failed. Please try again.';
        toast.error(msg);
        setPurchasing(false);
        setActivePkg(null);
      });
      rzp.open();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not start checkout';
      toast.error(typeof msg === 'string' ? msg : 'Could not start checkout');
      setPurchasing(false);
      setActivePkg(null);
    }
  };

  if (!user) return null;

  return (
    <div data-testid="credits-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      {/* Header */}
      <div className="max-w-2xl">
        <span className="text-[11px] uppercase tracking-[0.22em] text-rose-gold flex items-center gap-2">
          <Wallet className="h-3.5 w-3.5" /> Credits
        </span>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl font-semibold text-ink leading-[1.05]">
          Power your beauty AI.
        </h1>
        <p className="mt-3 text-muted-foreground leading-relaxed">
          Top up credits to use Ask AI, Photo Styling and Regimen generation. 1 credit = $1 USD or ₹80 INR.
        </p>
      </div>

      {/* Balance + currency */}
      <Card className="mt-8 bg-card rounded-2xl border-border/70 p-5 sm:p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-accent/60 border border-rose-tint flex items-center justify-center">
              <Sparkles className="h-6 w-6 text-rose-gold" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Your balance</p>
              <p className="font-display text-3xl text-ink leading-tight tabular-nums" data-testid="credits-current-balance">
                {credits} <span className="text-base font-sans text-muted-foreground">credit{credits === 1 ? '' : 's'}</span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Currency</span>
            <ToggleGroup
              type="single"
              value={currency || 'USD'}
              onValueChange={(v) => v && setCurrency(v)}
              className="bg-secondary rounded-xl border border-border/70 p-1"
              data-testid="credits-currency-toggle"
            >
              <ToggleGroupItem value="USD" className="rounded-lg data-[state=on]:bg-card text-xs px-3" data-testid="currency-usd">
                USD ($)
              </ToggleGroupItem>
              <ToggleGroupItem value="INR" className="rounded-lg data-[state=on]:bg-card text-xs px-3" data-testid="currency-inr">
                INR (₹)
              </ToggleGroupItem>
            </ToggleGroup>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => refreshCredits()}
              data-testid="credits-refresh-button"
              className="rounded-full"
              aria-label="Refresh balance"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Cost breakdown */}
        {config?.feature_costs && (
          <div className="mt-5 pt-5 border-t border-border/70 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-muted-foreground">
            <div className="flex items-center justify-between rounded-lg bg-cream-paper border border-border/70 px-3 py-2">
              <span>Ask AI</span>
              <Badge className="bg-secondary text-foreground border border-border text-[10px]">
                {config.feature_costs.qa_ask} credit
              </Badge>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-cream-paper border border-border/70 px-3 py-2">
              <span>AI Styling</span>
              <Badge className="bg-secondary text-foreground border border-border text-[10px]">
                {config.feature_costs.styling_analyze} credits
              </Badge>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-cream-paper border border-border/70 px-3 py-2">
              <span>Regimen</span>
              <Badge className="bg-secondary text-foreground border border-border text-[10px]">
                {config.feature_costs.regimen_generate} credits
              </Badge>
            </div>
          </div>
        )}

        {/* Payment methods accepted */}
        <div className="mt-5 pt-5 border-t border-border/70 flex flex-col sm:flex-row sm:items-center gap-3 text-xs text-muted-foreground" data-testid="payment-methods-row">
          <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground shrink-0">
            We accept
          </span>
          <div className="flex flex-wrap gap-1.5">
            {currency === 'INR' ? (
              <>
                <Badge className="bg-cream-paper text-foreground border border-border/70 text-[10px]">UPI / GPay</Badge>
                <Badge className="bg-cream-paper text-foreground border border-border/70 text-[10px]">PhonePe</Badge>
                <Badge className="bg-cream-paper text-foreground border border-border/70 text-[10px]">Paytm</Badge>
                <Badge className="bg-cream-paper text-foreground border border-border/70 text-[10px]">NetBanking</Badge>
                <Badge className="bg-cream-paper text-foreground border border-border/70 text-[10px]">Cards</Badge>
                <Badge className="bg-cream-paper text-foreground border border-border/70 text-[10px]">Wallets</Badge>
              </>
            ) : (
              <>
                <Badge className="bg-cream-paper text-foreground border border-border/70 text-[10px]">Visa / Mastercard / Amex</Badge>
                <Badge className="bg-cream-paper text-foreground border border-border/70 text-[10px]">International cards</Badge>
                <Badge className="bg-secondary text-muted-foreground border border-border/70 text-[10px]">Switch to INR for UPI / GPay</Badge>
              </>
            )}
          </div>
        </div>
      </Card>

      {/* Tabs: Buy / Custom / History */}
      <Tabs defaultValue="packages" className="mt-8">
        <TabsList className="bg-secondary rounded-xl p-1 border border-border/70 grid grid-cols-3 max-w-md">
          <TabsTrigger value="packages" className="rounded-lg data-[state=active]:bg-card text-xs" data-testid="credits-tab-packages">
            <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Packages
          </TabsTrigger>
          <TabsTrigger value="custom" className="rounded-lg data-[state=active]:bg-card text-xs" data-testid="credits-tab-custom">
            Custom
          </TabsTrigger>
          <TabsTrigger value="history" className="rounded-lg data-[state=active]:bg-card text-xs" data-testid="credits-tab-history">
            <History className="h-3.5 w-3.5 mr-1.5" /> History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="packages" className="mt-6">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-72 rounded-2xl" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
              {packages.map((p) => {
                const Icon = PACKAGE_ICONS[p.id] || Sparkles;
                const isPro = p.id === 'pro';
                return (
                  <Card
                    key={p.id}
                    className={`rounded-2xl p-6 border-border/70 relative overflow-hidden transition-all ${
                      isPro ? 'bg-cream-paper border-rose-tint shadow-md' : 'bg-card hover:shadow-md'
                    }`}
                    data-testid={`credits-package-${p.id}`}
                  >
                    {isPro && (
                      <Badge className="absolute top-4 right-4 bg-primary text-primary-foreground text-[10px] uppercase tracking-[0.14em]">
                        Most popular
                      </Badge>
                    )}
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-xl bg-accent/60 border border-rose-tint flex items-center justify-center">
                        <Icon className="h-5 w-5 text-rose-gold" />
                      </div>
                      <h3 className="font-display text-2xl text-ink">{p.name}</h3>
                    </div>
                    <p className="mt-1.5 text-xs text-muted-foreground">{p.tagline}</p>

                    <div className="mt-5">
                      <p className="font-display text-4xl text-ink tabular-nums">
                        {formatMoney(p.amount_display, p.currency)}
                      </p>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        for{' '}
                        <span className="font-semibold text-foreground tabular-nums">{p.credits}</span>{' '}
                        credit{p.credits === 1 ? '' : 's'}
                        {p.bonus > 0 && (
                          <span className="ml-1 text-rose-gold font-medium">
                            (+{p.bonus} bonus)
                          </span>
                        )}
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-1">
                        {formatMoney(p.price_per_credit_display, p.currency)} per credit
                      </p>
                    </div>

                    <ul className="mt-5 space-y-1.5 text-xs text-foreground/80">
                      <li className="flex items-start gap-1.5">
                        <Check className="h-3.5 w-3.5 mt-0.5 text-rose-gold shrink-0" />
                        ~{Math.floor(p.credits / 1)} Ask AI questions
                      </li>
                      <li className="flex items-start gap-1.5">
                        <Check className="h-3.5 w-3.5 mt-0.5 text-rose-gold shrink-0" />
                        ~{Math.floor(p.credits / 2)} Styling analyses or Regimens
                      </li>
                      <li className="flex items-start gap-1.5">
                        <Check className="h-3.5 w-3.5 mt-0.5 text-rose-gold shrink-0" />
                        Credits never expire
                      </li>
                    </ul>

                    <Button
                      onClick={() => startCheckout({ packageId: p.id })}
                      disabled={purchasing}
                      className={`mt-6 w-full rounded-xl ${
                        isPro
                          ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                          : 'bg-foreground text-background hover:bg-foreground/90'
                      }`}
                      data-testid={`buy-${p.id}-button`}
                    >
                      {purchasing && activePkg === p.id ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> Opening checkout
                        </>
                      ) : (
                        <>
                          Buy {p.name} <ArrowRight className="h-4 w-4 ml-1.5" />
                        </>
                      )}
                    </Button>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="custom" className="mt-6">
          <Card className="rounded-2xl bg-card border-border/70 p-6 max-w-xl">
            <h3 className="font-display text-2xl text-ink">Choose your amount</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Pick exactly how many credits you want. 1 credit = {currency === 'INR' ? '₹80' : '$1'}.
            </p>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
              <div>
                <label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground" htmlFor="custom-credits-input">
                  Credits
                </label>
                <Input
                  id="custom-credits-input"
                  type="number"
                  min={1}
                  max={1000}
                  value={customCredits}
                  onChange={(e) => setCustomCredits(Math.max(1, Math.min(1000, Number(e.target.value) || 1)))}
                  className="mt-1 h-11 rounded-xl bg-card text-lg tabular-nums"
                  data-testid="custom-credits-input"
                />
              </div>
              <div className="text-right">
                <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Total</p>
                <p className="font-display text-3xl text-ink tabular-nums" data-testid="custom-total-amount">
                  {formatMoney(customAmountDisplay, currency || 'USD')}
                </p>
              </div>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              {[5, 10, 25, 50, 100].map((n) => (
                <button
                  key={n}
                  onClick={() => setCustomCredits(n)}
                  className={`text-xs rounded-full border px-3 py-1.5 transition-colors ${
                    customCredits === n
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-cream-paper border-border/70 hover:bg-secondary'
                  }`}
                  data-testid={`custom-preset-${n}`}
                >
                  {n} credits
                </button>
              ))}
            </div>
            <Button
              onClick={() => startCheckout({ customAmount: customCredits })}
              disabled={purchasing || !customCredits || customCredits < 1}
              className="mt-6 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 w-full sm:w-auto"
              data-testid="buy-custom-button"
            >
              {purchasing && activePkg === 'custom' ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> Opening checkout
                </>
              ) : (
                <>
                  Buy {customCredits} credit{customCredits === 1 ? '' : 's'} <ArrowRight className="h-4 w-4 ml-1.5" />
                </>
              )}
            </Button>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          <Card className="rounded-2xl bg-card border-border/70 p-6 max-w-3xl">
            <h3 className="font-display text-2xl text-ink">Recent activity</h3>
            <ScrollArea className="mt-4 max-h-[420px]">
              {historyLoading ? (
                <div className="space-y-2">
                  {[0, 1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-12 rounded-lg" />
                  ))}
                </div>
              ) : history.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  No activity yet. Your purchases and AI usage will appear here.
                </p>
              ) : (
                <ul className="divide-y divide-border/60">
                  {history.map((h) => (
                    <li
                      key={h.id || `${h.created_at}-${h.kind}`}
                      className="py-3 flex items-center justify-between gap-3 text-sm"
                      data-testid="history-row"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium capitalize">
                          {prettyKind(h.kind, h.feature)}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          {new Date(h.created_at).toLocaleString()}
                          {h.feature ? ` · ${h.feature.replace(/_/g, ' ')}` : ''}
                        </p>
                      </div>
                      <span
                        className={`tabular-nums font-semibold ${
                          (h.credits || 0) >= 0 ? 'text-emerald-700' : 'text-foreground/80'
                        }`}
                      >
                        {(h.credits || 0) > 0 ? '+' : ''}
                        {h.credits} cr
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </ScrollArea>
          </Card>
        </TabsContent>
      </Tabs>

      <p className="mt-8 text-[11px] text-muted-foreground text-center">
        Payments are processed securely by Razorpay. We never store your card details.
        {' '}
        <Link to="/profile" className="underline underline-offset-4">Manage profile</Link>
      </p>
    </div>
  );
}

function prettyKind(kind, feature) {
  if (kind === 'purchase') return 'Credits purchased';
  if (kind === 'spend') return `Used for ${feature ? feature.replace(/_/g, ' ') : 'AI feature'}`;
  if (kind === 'refund') return 'Refund (AI failed)';
  if (kind === 'welcome') return 'Welcome bonus';
  if (kind === 'order_created') return 'Order started';
  return kind || 'Activity';
}
